import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import LogoPc from "../assets/images/icons/mercury-pc.png";
import LogoMb from "../assets/images/icons/mercury-mb.svg";

import {
  GetDataMenuData,
  GetDataActiveUserGroup,
} from "../components/utils/data-storage";
import ScreenService from "../services/screenService";

const MENU_ORDER = ['all request', 'report', 'master data', 'settings'];
const SUBMENU_ORDER = ['company', 'building', 'bank', 'service provider', 'messenger', 'holiday'];
const REPORT_SUBMENU_ORDER = ['dashboard', 'service report', 'report requests'];

// ── FIX: เขียน tree-builder เองแทนการพึ่ง ConvertDataMenuForShow (format-value.js) ──
// ── เพราะ ConvertDataMenuForShow มีปัญหากับข้อมูลจริงจาก DB ที่ sub_module_id ซ้ำกันได้ในกลุ่มเดียวกัน ──
// ── (เช่น module_id=5 มีทั้ง id 15,16,17 เป็น sub_module_id=5 เหมือนกันหมด) ทำให้ error 'sub_module_id' undefined ──
// ── ฟังก์ชันนี้ควบคุมเองทั้งหมด รับ array ของแถว screens (module_id, sub_module_id, code, name, page_url, ...) ──
// ── group ตาม module_id แล้วถือว่า "header" ของกลุ่ม คือแถวที่ sub_module_id === 0 (ถ้าหาไม่เจอ fallback เป็นแถวแรกกัน crash) ──
// ── ที่เหลือในกลุ่มกลายเป็น subItems ทั้งหมด ไม่สนใจว่า sub_module_id จะซ้ำกันหรือไม่ (ใช้แค่แยก header กับ children) ──
const buildMenuFromScreens = (screensList) => {
  const byModule = {};
  (screensList || []).forEach((s) => {
    if (!s) return;
    if (!byModule[s.module_id]) byModule[s.module_id] = [];
    byModule[s.module_id].push(s);
  });

  return Object.values(byModule).map((group) => {
    const header = group.find((s) => s.sub_module_id === 0) || group[0];
    const children = group.filter((s) => s !== header);

    return {
      code: header.code,
      name: header.name,
      page_url: header.page_url,
      page_param: header.page_param,
      page_group: header.page_group,
      icon_name: header.icon_name,
      subItems: children.map((c) => ({
        code: c.code,
        name: c.name,
        page_url: c.page_url,
        page_param: c.page_param,
        page_group: c.page_group,
        icon_name: c.icon_name,
      })),
    };
  });
};

const Sidebar = () => {
  // ── FIX: ดึงเมนูจาก DB จริงผ่าน /map-group-screens แทนการอ่านจาก localStorage cache (mnd) ──
  // ── response ของ /map-group-screens แนบข้อมูล screen (module_id, sub_module_id, code, name, page_url, ...) มาให้ในฟิลด์ `screen` อยู่แล้ว ──
  // ── ไม่ต้องเรียก /screens แยกอีกรอบ และไม่ต้องกรองด้วย is_menu เลย (จากข้อมูลจริง is_menu=1 มีแค่ "หัวข้อกลุ่ม" เช่น Master Data/Report ──
  // ── ส่วนรายการจริงอย่าง All request, Company, Bank ฯลฯ เป็น is_menu=0 ทั้งหมด กรองด้วย is_menu เลยทำเมนูหายไปเกือบหมด) ──
  // ── กรองแค่ screen.is_show === 1 (ตัด "Request detail" ที่เป็นหน้า detail ไม่ใช่ลิงก์ในเมนู) กับ screen.status === 1 ──
  const [screens, setScreens] = useState([]);

  // ── FIX: ใช้ "active user group" (คนละตัวกับ GetDataUserGroup ที่เป็นค่าจริงตอน login) ──
  // ── เพราะตอน admin กด "สลับไปดูเป็น User" ต้องเปลี่ยนแค่เมนูที่เห็น ไม่ใช่ id_user_group ตัวจริง ──
  // ── (ดูรายละเอียดที่ DdlProfile.jsx / data-storage.js) ── ใส่เป็น dependency ของ effect ดึงเมนูด้านล่าง ──
  // ── เพื่อให้ "สลับ role แล้ว re-render" (มาพร้อมการ navigate ไปหน้าใหม่อยู่แล้ว) effect รันซ้ำ ดึงเมนูของ group ล่าสุดทันที ──
  const userGroup = GetDataActiveUserGroup();

  useEffect(() => {
    let cancelled = false;

    const fetchMenu = async () => {
      try {
        // ดึงเมนูที่ user group นี้มีสิทธิ์ (is_view=1) มาทีเดียว พร้อมข้อมูล screen แนบมาในตัว
        const permResp = await ScreenService.GetMapGroupScreens({
          id_user_group: userGroup,
          is_view: 1,
          status: 1,
          limit: 500,
        });

        const permittedScreens = (permResp?.data?.result || [])
          .map((m) => m.screen)
          .filter((s) => s && s.is_show === 1 && s.status === 1);

        if (!cancelled) setScreens(permittedScreens);
      } catch (err) {
        console.error('โหลดเมนูจาก DB ไม่สำเร็จ:', err);
        // ── fallback: ถ้า API ล่ม/เน็ตหลุด ใช้ค่า cache เดิมใน localStorage แทน ไม่ให้ sidebar ว่างเปล่า ──
        const cached = GetDataMenuData();
        if (!cancelled && cached) {
          try {
            setScreens(JSON.parse(atob(cached)));
          } catch { /* ignore parse error, ปล่อย screens ว่างไป */ }
        }
      }
    };

    fetchMenu();
    return () => { cancelled = true; };
  }, [userGroup]);

    const dataMenu = useMemo(() => {
      // ── กัน error ตอนยังโหลดจาก DB ไม่เสร็จ (screens ยังว่างอยู่) ──
      const raw = screens.length > 0 ? buildMenuFromScreens(screens) : [];

      return raw
        .filter(item => item.name?.toLowerCase() !== "dashboard")
        .map(item => ({
          ...item,
          subItems: (item.subItems?.filter(s => {
              if (s.page_url?.includes("param")) return false;
              // TODO: ซ่อน Report requests ชั่วคราว
              // if (s.page_url === '/admin/report-requests') return false;
              return true;
          }) ?? [])
            .sort((a, b) => {
              const order = item.name?.toLowerCase() === 'report'
                  ? REPORT_SUBMENU_ORDER
                  : SUBMENU_ORDER;
              const ai = order.indexOf(a.name?.toLowerCase());
              const bi = order.indexOf(b.name?.toLowerCase());
              return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
            })
        }));
    }, [screens]);

  const fullMenu = useMemo(() => {
    return [...dataMenu].sort((a, b) => {
      const ai = MENU_ORDER.indexOf(a.name?.toLowerCase());
      const bi = MENU_ORDER.indexOf(b.name?.toLowerCase());
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });
  }, [dataMenu]);

  const { pathname } = useLocation();

  // ── subMenuCurrentState: เมนูหลักไหน "active" อยู่ตอนนี้ (ผูกกับ pathname/section ปัจจุบัน) ──
  // ── ค่านี้ไม่ควรถูกเคลียร์ทิ้งเวลาผู้ใช้แค่ปิด flyout เฉยๆ ไม่งั้นไอคอนเมนูหลักจะหลุด active ไปด้วย ──
  const [subMenuCurrentState, setSubMenuCurrentState] = useState("");
  // ── openMenuCode: เมนูไหนกำลังกาง flyout/accordion อยู่ตอนนี้ (แค่เรื่อง "เปิด/ปิด" ล้วนๆ) ──
  // ── แยกออกจาก subMenuCurrentState เพื่อให้ปิด flyout ได้โดยไม่กระทบ active state ของเมนูหลัก ──
  const [openMenuCode, setOpenMenuCode] = useState("");
  const [urlDashboard, setUrlDashboard] = useState("/admin/requests");
  const [isMobile, setIsMobile] = useState(false);

  // ── DESIGN: จอเล็ก/responsive (< 1200px) = icon-only rail + flyout submenu (ตามภาพ mockup) ──
  // ── desktop (>= 1200px) ไม่แตะต้องเลย ยังเป็น sidebar เต็มความกว้าง มีตัวหนังสือ เหมือนเดิมทุกอย่าง ──
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1200);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ── ปิด flyout อัตโนมัติเมื่อผู้ใช้แตะ/คลิกที่อื่นนอก sidebar (เฉพาะตอนเป็น icon rail บนจอเล็ก) ──
  // ── ไม่งั้น flyout จะค้างเปิดอยู่จนกว่าจะกด icon อื่น ── เคลียร์แค่ openMenuCode (การเปิด/ปิด flyout) ──
  // ── ไม่แตะ subMenuCurrentState เพื่อให้เมนูหลักยัง active ค้างไว้ตามหน้าปัจจุบัน ──
  useEffect(() => {
    if (!isMobile) return;
    const handleClickOutside = (e) => {
      const sidebarEl = document.getElementById("sidebar");
      if (sidebarEl && !sidebarEl.contains(e.target)) {
        setOpenMenuCode("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMobile]);

  useEffect(() => {
    const allRequest = dataMenu.find(
      (e) => e.name?.toLowerCase() === "all request"
    );
    setUrlDashboard(allRequest ? allRequest.page_url : "/admin/requests");
  }, [dataMenu, userGroup]);

  const isMenuActive = useCallback((url) => {
    if (!url) return false;
    if (url.includes("/param")) {
      return pathname.startsWith(url.replace("/param", ""));
    }
    return pathname === url;
  }, [pathname]);

  // ── เมนู "ไม่มี submenu" (เช่น All request) กับ "มี submenu" (เช่น Report) ใช้ single source of truth ──
  // ── เดียวกันคือ subMenuCurrentState === item.code ตัดสิน active ทั้งคู่ ── กด menu ไหนก็ active menu นั้นทันที ──
  // ── useEffect นี้ sync subMenuCurrentState ให้ตรงกับ pathname เองเวลา navigate ผ่าน Link ──
  useEffect(() => {
    let activeCode = "";
    for (const item of fullMenu) {
      if (isMenuActive(item.page_url)) {
        activeCode = item.code;
        break;
      }
      if (item.subItems?.length > 0) {
        const matched = item.subItems.find((s) => isMenuActive(s.page_url));
        if (matched) {
          activeCode = item.code;
          break;
        }
      }
    }
    if (activeCode !== "") {
      setSubMenuCurrentState(activeCode);
      // ── FIX: auto-expand accordion เฉพาะ desktop เท่านั้น ──
      // ── ตอนแรก effect นี้ setOpenMenuCode(activeCode) ทุกครั้งที่ pathname เปลี่ยน รวมถึงตอน mobile ด้วย ──
      // ── ทำให้พอกด submenu link (ซึ่งสั่งปิด flyout เอง) แล้ว navigate ไป pathname เปลี่ยน ──
      // ── effect นี้ทำงานตามหลัง แล้วเปิด flyout กลับมาใหม่ทันที (ค้างเปิดตามที่แจ้งมา) ──
      // ── mobile เลยต้องปล่อยให้ openMenuCode ถูกคุมโดยการกดของ user เองล้วนๆ (toggle/เลือก submenu/คลิกนอก sidebar) ──
      if (!isMobile) {
        setOpenMenuCode(activeCode);
      }
    }
  }, [pathname, fullMenu, isMenuActive, isMobile]);

  const OnClickSubMenu = (e, code) => {
    e.preventDefault();
    setSubMenuCurrentState((prev) => (prev === code ? "" : code));
    setOpenMenuCode((prev) => (prev === code ? "" : code));
  };

  const getMenuIcon = (name) => {
    const n = name?.toLowerCase();
    if (n === 'all request')  return 'ri-layout-masonry-line';
    if (n === 'report')       return 'ri-pie-chart-line';
    if (n === 'master data')  return 'mdi mdi-office-building-outline';
    if (n === 'settings')     return 'ri-settings-3-line';
    return 'ri-layout-masonry-line';
  };

  // console.log('menu:', JSON.stringify(dataMenu));

  // console.log('subItems master data:', JSON.stringify(
  //   dataMenu.find(m => m.name?.toLowerCase() === 'master data')?.subItems
  // ));

  const sidebarClassName = ["active", isMobile ? "collapsed" : ""]
    .filter(Boolean)
    .join(" ");

  return (
    <div id="sidebar" className={sidebarClassName}>
      <div className="sidebar-wrapper">
        {/* Header */}
        <div className="sidebar-header">
          <div className="logo">
            <Link to={urlDashboard}>
              <img src={isMobile ? LogoMb : LogoPc} alt="logo" />
            </Link>
          </div>
        </div>

        {/* Menu */}
        <div className="sidebar-menu">
          <ul className="menu">
            {fullMenu.map((item, index) => (
              <React.Fragment key={index}>
                {item.subItems?.length > 0 ? (
                  // --- มี submenu ---
                  <li className={`sidebar-item has-sub ${subMenuCurrentState === item.code ? "active" : ""}`}>
                    <button
                      className="sidebar-link"
                      onClick={(e) => OnClickSubMenu(e, item.code)}
                      type="button"
                      title={isMobile ? item.name : undefined}
                    >
                      <i className={getMenuIcon(item.name)}></i>
                      <span>{item.name}</span>
                    </button>

                    {/* ── flyout/accordion เปิด-ปิดตาม openMenuCode (แยกจาก subMenuCurrentState ที่คุม active highlight) ── */}
                    <ul className={`submenu ${openMenuCode === item.code ? "active" : ""}`}>
                      {/* ── highlight submenu-item ตัวที่ตรงกับหน้าปัจจุบัน (โชว์ทั้ง PC และจอเล็ก) ── */}
                      {item.subItems.map((sub, subIndex) => (
                        <li
                          key={subIndex}
                          className={`submenu-item ${isMenuActive(sub.page_url) ? "active" : ""}`}
                        >
                          <Link
                            to={sub.page_url}
                            onClick={() => {
                              // ── ตอนเป็น flyout (จอเล็ก) ปิดทันทีหลังกดเลือก แต่ไม่แตะ subMenuCurrentState ──
                              // ── เมนูหลัก (ไอคอน) เลยยัง active ค้างไว้ตามหน้าที่เพิ่งไปถูกต้อง ──
                              if (isMobile) setOpenMenuCode("");
                            }}
                          >
                            {sub.name}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </li>
                ) : (
                  // --- ไม่มี submenu ---
                  <li className={`sidebar-item ${subMenuCurrentState === item.code ? "active" : ""}`}>
                    <Link
                      to={item.page_url}
                      className="sidebar-link"
                      title={isMobile ? item.name : undefined}
                      onClick={() => {
                        // ── FIX: ต้นเหตุจริงของ "กดเมนูไม่มี submenu ไม่ติด" ──
                        // ── subMenuCurrentState (คุม highlight) เดิมอัปเดตแค่ตอน pathname เปลี่ยนจริงเท่านั้น ──
                        // ── ถ้ากดเมนูที่ "ตัวเองอยู่หน้านั้นอยู่แล้ว" (เช่นเพิ่งกด Report ไป แต่จริงๆ ยังอยู่หน้า /admin/requests) ──
                        // ── pathname จะไม่เปลี่ยนเลย useEffect ที่ sync ไม่ทำงาน highlight เลยค้างอยู่ที่เมนูก่อนหน้า ──
                        // ── ดูเหมือนกดไม่ติด ทั้งที่จริง Link นำทางปกติ แค่ไม่มีอะไรให้เปลี่ยนเพราะอยู่หน้านั้นอยู่แล้ว ──
                        // ── เซ็ต highlight ให้ตัวเองทันทีตรงนี้เลย ไม่ต้องรอ pathname sync ──
                        setSubMenuCurrentState(item.code);
                        // ── ปิด flyout/accordion ของ item อื่นที่อาจค้างเปิดอยู่ด้วย (ทั้ง desktop และ mobile) ──
                        setOpenMenuCode("");
                      }}
                    >
                      <i className={getMenuIcon(item.name)}></i>
                      <span>{item.name}</span>
                    </Link>
                  </li>
                )}
              </React.Fragment>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;